/**
 * Web-push subscribe/unsubscribe helpers for Mango Personal (Social Circle) accounts.
 * Backed by public/service-worker.js (push-event handling only) + personal-push.routes.ts.
 */
import { personalPushApi } from '../services/api';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration('/service-worker.js');
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}

export async function subscribeToPersonalPush(): Promise<void> {
  if (!isPushSupported()) throw new Error('Push notifications are not supported in this browser');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission denied');

  const registration = await navigator.serviceWorker.register('/service-worker.js');
  await navigator.serviceWorker.ready;

  const keyRes = await personalPushApi.getVapidPublicKey();
  const publicKey = keyRes?.publicKey || keyRes?.data?.publicKey;
  if (!publicKey) throw new Error('Push not configured on the server');

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  const res = await personalPushApi.subscribe(subscription.toJSON() as PushSubscriptionJSON);
  if (!res?.success) throw new Error(res?.error || 'Failed to register subscription');
}

export async function unsubscribeFromPersonalPush(): Promise<void> {
  const subscription = await getCurrentPushSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  await personalPushApi.unsubscribe(endpoint);
}
